// UC-163 Work Item 6 (QA #12) — HD-4 eligibility predicate.
//
// Variant V is eligible as a mapping candidate for identifier I in
// organisation O at time T iff, computed from CURRENT state with no cached
// mapping state (artifacts/12/build-plans/UC-163.md § "HD-4 eligibility
// predicate — the core contract"):
//   1. V belongs to O
//   2. V exists and is not soft-deleted
//   3. O is not deleted or archived
//   4. No active product_identifier_links row binds I to a DIFFERENT variant in O
//   5. V having an existing active mapping to a DIFFERENT source identifier
//      does NOT make it ineligible
//
// Clause 5 is the QA #12 fix: the picker must never exclude a variant merely
// because it already carries some other active mapping. This module is the
// single, real implementation of that contract (previously there was no
// eligibility filtering code at all — see build-state.json UC-163 rework
// notes) — `useVariantCatalog`/`SuggestedMatchPicker` call `isVariantEligible`
// per candidate rather than re-deriving these rules ad hoc.
//
// Schema note: `master_products` has no soft-delete column and
// `organisations` has no deleted/archived column today, so clauses 2 and 3
// are honoured structurally (a variant/organisation that no longer exists in
// the current-state query simply isn't a candidate) rather than via an
// explicit boolean flag. The predicate still accepts optional flags so it
// degrades correctly if/when such columns are added, instead of silently
// treating every row as eligible forever.

export interface EligibilityContext {
  /** The organisation the candidate is being evaluated for (I's organisation). */
  organisationId: string;
  /** The candidate variant's own organisation_id. */
  variantOrganisationId: string;
  /** True when the variant row was soft-deleted (clause 2). Defaults to false — no such column exists today. */
  variantSoftDeleted?: boolean;
  /** True when the organisation was soft-deleted/archived (clause 3). Defaults to false — no such column exists today. */
  organisationDeletedOrArchived?: boolean;
  /**
   * True when this variant already carries an active mapping to some OTHER
   * identifier than the one currently being staged. Documented explicitly
   * so callers (and tests) can assert clause 5 directly: this flag must
   * never influence the result.
   */
  hasActiveMappingToOtherIdentifier?: boolean;
}

/**
 * HD-4 eligibility predicate, clauses 1-3 and 5. (Clause 4 — "no active link
 * binds I to a different variant" — is enforced structurally by the
 * database's UNIQUE(organisation_id, identifier_type, identifier_value)
 * constraint on `product_identifier_links`: an identifier can only carry one
 * active link at a time, so it is not something this per-candidate predicate
 * needs to re-check.)
 */
export function isVariantEligible(ctx: EligibilityContext): boolean {
  if (ctx.variantOrganisationId !== ctx.organisationId) return false; // clause 1
  if (ctx.variantSoftDeleted) return false; // clause 2
  if (ctx.organisationDeletedOrArchived) return false; // clause 3

  // clause 5 — intentionally NOT checked. `hasActiveMappingToOtherIdentifier`
  // is accepted only so tests can assert this explicitly; it must never
  // gate the return value below.
  return true;
}

export interface EligibleCandidateInput {
  master_product_id: string;
  organisation_id: string;
}

/**
 * Filters a candidate list down to the eligible subset for organisation
 * `organisationId`, computed fresh from the input each call — never cached
 * across calls (HD-4's "computed from current state" contract). Callers are
 * expected to pass an already-live (react-query) list so this recomputes on
 * every render/refetch rather than memoizing stale mapping state.
 */
export function filterEligibleCandidates<T extends EligibleCandidateInput>(
  candidates: T[],
  organisationId: string,
  deletedMasterProductIds: ReadonlySet<string> = new Set(),
  organisationDeletedOrArchived = false,
): T[] {
  return candidates.filter((candidate) =>
    isVariantEligible({
      organisationId,
      variantOrganisationId: candidate.organisation_id,
      variantSoftDeleted: deletedMasterProductIds.has(candidate.master_product_id),
      organisationDeletedOrArchived,
    }),
  );
}
